import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';
import { Button, EmptyState, PageHeader, Pagination, SegmentedControl } from '@/shared/components';
import { useApi, toApiError, type OutboxFilterStatus } from '@/shared/api';
import { ChannexDeadLetterSection } from './ChannexDeadLetterSection';
import { OutboxTable } from './OutboxTable';
import { outboxStatusLabel } from './systemLabels';

const STATUS_TABS: OutboxFilterStatus[] = ['Pending', 'Processed', 'Failed', 'All'];
const statusOptions = STATUS_TABS.map((value) => ({ value, label: outboxStatusLabel(value) }));

export function OutboxPage() {
  const api = useApi();
  const qc = useQueryClient();
  const [status, setStatus] = useState<OutboxFilterStatus>('Failed');
  const [page, setPage] = useState(1);
  const [retryingId, setRetryingId] = useState<string | null>(null);
  const pageSize = 25;

  const { data, isLoading, error } = useQuery({
    queryKey: ['system', 'outbox', status, page],
    queryFn: () => api.system.outboxList(status, page, pageSize),
    placeholderData: (prev) => prev,
  });

  const reportError = async (err: unknown) => {
    const e = await toApiError(err);
    toast.error(e.detail ?? e.title);
  };

  const retryOne = useMutation({
    mutationFn: (id: string) => api.system.outboxRetry(id),
    onSuccess: () => {
      toast.success('Mail erneut eingereiht');
      void qc.invalidateQueries({ queryKey: ['system', 'outbox'] });
    },
    onError: reportError,
    onSettled: () => setRetryingId(null),
  });

  const retryAll = useMutation({
    mutationFn: () => api.system.outboxRetryAllFailed(),
    onSuccess: (result) => {
      toast.success(
        `${result.affected} ${result.affected === 1 ? 'Mail' : 'Mails'} erneut eingereiht`,
      );
      void qc.invalidateQueries({ queryKey: ['system', 'outbox'] });
    },
    onError: reportError,
  });

  return (
    <>
      <PageHeader
        eyebrow="System"
        title="Mail"
        titleAccent="Ausgang"
        description={
          data
            ? `${data.total} Mails im gewählten Zustand. Der Versand läuft im Hintergrund und sieht alle fünf Sekunden nach.`
            : 'Mails werden geladen…'
        }
        actions={
          status === 'Failed' && data && data.total > 0 ? (
            <Button variant="brand" onClick={() => retryAll.mutate()} loading={retryAll.isPending}>
              Alle gescheiterten erneut senden
            </Button>
          ) : null
        }
      />

      <div className="mb-4">
        <SegmentedControl
          value={status}
          onChange={(next) => {
            setStatus(next);
            setPage(1);
          }}
          options={statusOptions}
          className="[&>button]:min-h-11"
        />
      </div>

      {error ? (
        <EmptyState icon="Error" title="Konnte den Mail-Ausgang nicht laden" />
      ) : !isLoading && data && data.items.length === 0 ? (
        <EmptyState
          icon="Inbox"
          title="Keine Mails"
          description={
            status === 'Failed'
              ? 'Keine gescheiterte Mail — hier steht im Normalfall nichts.'
              : 'Für diesen Zustand liegt gerade nichts vor.'
          }
        />
      ) : (
        <>
          <OutboxTable
            messages={data?.items ?? []}
            retryingId={retryingId}
            onRetry={(id) => {
              setRetryingId(id);
              retryOne.mutate(id);
            }}
          />
          {data && (
            <Pagination
              page={page}
              pageSize={pageSize}
              total={Number(data.total)}
              onPageChange={setPage}
            />
          )}
        </>
      )}

      <ChannexDeadLetterSection />
    </>
  );
}
